// dependencies.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { format } from 'date-fns'
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
// components.
import AdminTemplate from '../../templates/AdminPanel'
import {
  CardsDesktopItem,
  CardsDesktopSort,
  CardsDesktopWrapper,
  CardsMobileItem,
  CardsMobileSort,
  CardsMobileWrapper,
} from '../../components/screens/Cards'
import { Drawer, PreviewFrame, PREVIEWFRAME_HEIGHT, ShowFrame } from '../../components/Modal'
import { Loader } from '../../components/Icon'
import { ReportCard } from '../../components/Card'
import { Form } from '../../components/Form'
import { FilterList } from '../../components/Typography/FilterChip'
import Header from '../../components/Layout/Header'
import ToggleFilterButton from '../../components/Filters/ToggleFilterAsideButton'
// import { BranchSelect } from '../../components/Form'
import FilterDate from '../../components/Filters/Date'
import FilterCardType from '../../components/Filters/CardType'
import FilterStatus from '../../components/Filters/Status'
import FilterResponsible from '../../components/Filters/Responsible'
import FilterLocation from '../../components/Filters/Location'
import ToggleSortingButton from '../../components/Filters/ToggleSortingTooltipButton'
import SearchCardsById from '../../components/screens/Cards/SearchCardsById'
// utils.
import notifyToast from '../../js/notifyToast'
import useFetchCardsData from '../../js/cards/hooks/useFetchCardsData'
import { db, getLocationById, getUserById, storage } from '../../js/firebase/firebase'
import { useDimensions } from '../../js/hooks'
import mailNotificationsMessage, {
  cardStatusChangeNotificationMessage,
} from '../../js/mailNotificationMessage'
import { useAppDispatch, useAppSelector } from '../../js/store/hooks'
import { cardUpdated, clearFilters, removeFilter, reload, setFilters } from '../../js/cards/cardsSlice'
import { selectUserData } from '../../js/auth/authSelectors'
import { selectUsersData, selectUsersForSelect } from '../../js/users/usersSelectors'
import {
  selectActiveFilters,
  selectCardTypeFilter,
  selectFilteredCards,
  selectFromDateFilter,
  selectLocationFilter,
  selectSearchTerm,
  selectStatusFilter,
  selectToDateFilter,
} from '../../js/cards/cardsSelectors'
// constants.
import {
  NAVIGATION_WIDTH,
  NAVIGATION_CONDENSED_WIDTH,
  FILTERS_ASIDE_WIDTH,
  HEADER_HEIGHT_DESKTOP,
  SITE_URL,
} from '../../constants'
import {
  CHANGE_CARD_RESPONSIBLE_FORM,
  EDIT_COMPLETE_CARD_FORM,
  EDIT_IN_PROCESS_CARD_FORM,
} from './constants'
import { selectCardsLocationsData, selectLocationsTree } from '../../js/locations/locationsSelectors'
import { selectAnomaliesData } from '../../js/anomalies/anomaliesSelectors'

const selectPriorities = [
  { label: 'Baja (60d)', name: 0 },
  { label: 'Media baja (30d)', name: 1 },
  { label: 'Media alta (7d)', name: 2 },
  { label: 'Alta (2d)', name: 3 },
]

// main component.
const AdminCardsScreen = ({ navMenu, navTitle }) => {
  const scrollRef = useRef()
  const { isMobile } = useDimensions()

  // fetch cards data (Firestore query scoped to filter date range).
  const loaded = useFetchCardsData({ filterByDateRange: true })
  const dispatch = useAppDispatch()

  const users = useAppSelector(selectUsersData)
  const userData = useAppSelector(selectUserData)
  const responsiblesSelectOptions = useAppSelector(selectUsersForSelect)
  const anomalies = useAppSelector(selectAnomaliesData)
  const locations = useAppSelector(selectCardsLocationsData)
  const filteredCardsSelector = selectFilteredCards(users, locations)
  const filteredCards = useAppSelector(filteredCardsSelector)
  const locationsTree = useAppSelector(selectLocationsTree)
  const activeFiltersSelector = selectActiveFilters(users, locationsTree)
  const activeFilters = useAppSelector(activeFiltersSelector)

  const [modalContent, setModalContent] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [focusedObject, setFocusedObject] = useState(null)
  const [offsetTop, setOffsetTop] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sortingOpen, setSortingOpen] = useState(false)
  const [navIsCondensed, setNavIsCondensed] = useState(false)
  const [files, setFiles] = useState([])
  const filesRef = useRef([])
  const [videos, setVideos] = useState([])
  const videosRef = useRef([])

  const [mobileCardStack, setMobileCardStack] = useState([])
  const [mobileCardStackKey, setMobileCardStackKey] = useState('')
  const [mobileCardIndex, setMobileCardIndex] = useState(0)

  const dismissCardSurfacesAfterStatusChange = () => {
    setFocusedObject(null)
    setMobileCardStackKey('')
    setMobileCardStack([])
    setMobileCardIndex(0)
  }

  const searchTerm = useAppSelector(selectSearchTerm)
  const cardTypeFilter = useAppSelector(selectCardTypeFilter)
  const fromDateFilter = useAppSelector(selectFromDateFilter)
  const toDateFilter = useAppSelector(selectToDateFilter)
  const locationFilter = useAppSelector(selectLocationFilter)
  const statusFilter = useAppSelector(selectStatusFilter)

  // open modal.
  const handleModalOpen = (open) => setModalOpen(open)

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    videosRef.current = videos
  }, [videos])

  const onFileUpload = (file, isVideo = false) => {
    if (!file) {
      setFiles([])
      return
    }

    return new Promise((resolve, reject) => {
      const currentTime = new Date()
      const fileName = `${format(currentTime, 'yyyyMMddHHmmss')}_${file.name}`
      const path = `cards/${fileName}`
      const storageRef = ref(storage, path)
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        () => {
          if (isVideo) setVideos([])
          else setFiles([])
        },
        (err) => reject(err),
        () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((url) => {
              if (url) {
                if (isVideo) setVideos([path])
                else setFiles([path])
                resolve(url)
              }
            })
            .catch((err) => reject(err))
        }
      )
    })
  }

  const deleteFile = (isVideo) => {
    if (isVideo) setVideos([])
    else setFiles([])
  }

  // ------------------------------------------------------------------------------------------- //
  // MODALS AND DRAWERS
  const handleChangeCardResponsibleModal = async (originalObj) => {
    const obj = {
      ...originalObj,
      responsibleId: originalObj.responsibleRef ? originalObj.responsibleRef.id : null,
    }

    setModalContent({
      title: 'Cambiar responsable',
      cardId: obj.ref.id,
      component: (
        <Form
          items={CHANGE_CARD_RESPONSIBLE_FORM(responsiblesSelectOptions)}
          defaultValues={obj}
          onClick={(obj) => handleChangeCardResponsibleClick(obj)}
        />
      ),
    })
    setModalOpen(true)
  }

  const handleChangeState = async (obj) => {
    const normalizedCard = {
      ...obj,
      cardType:
        obj.type === 'TS'
          ? 'Seguridad'
          : obj.type === 'TO'
            ? 'Órden y Limpieza'
            : obj.type === 'TQ'
              ? 'Calidad'
              : 'Mantenimiento',
    }

    const selectedAnomaliesFromRefs = (obj.anomaliesRefs || [])
      .map((anomalyRef) => anomalies.find((anomaly) => anomaly.ref?.id === anomalyRef.id))
      .filter(Boolean)
      .map((anomaly) => anomaly.name)
    const selectedAnomaliesFromCard = Array.isArray(obj.anomalies) ? obj.anomalies.filter(Boolean) : []
    const selectedAnomalies =
      selectedAnomaliesFromRefs.length > 0 ? selectedAnomaliesFromRefs : selectedAnomaliesFromCard

    const anomaliesSelectionState = selectedAnomalies.reduce((acc, anomalyName) => {
      acc[anomalyName] = true
      return acc
    }, {})
    const modalDefaultValues = {
      ...normalizedCard,
      anomalies: anomaliesSelectionState,
    }

    if (obj.status === 0) {
      setModalContent({
        title: 'Pasar tarjeta a en proceso',
        cardId: obj.ref.id,
        component: (
          <Form
            items={EDIT_IN_PROCESS_CARD_FORM(selectedAnomalies, selectPriorities)}
            defaultValues={modalDefaultValues}
            onClick={(values) => handleUpdateCardClick(values)}
          />
        ),
      })
    } else if (obj.status === 1) {
      setModalContent({
        title: 'Cerrar tarjeta',
        cardId: obj.ref.id,
        component: (
          <Form
            items={EDIT_COMPLETE_CARD_FORM(selectedAnomalies, selectPriorities)}
            defaultValues={modalDefaultValues}
            onClick={(values) => handleCompleteCardClick(values)}
            onFileUpload={onFileUpload}
            onFileDelete={deleteFile}
          />
        ),
      })
    }
    setModalOpen(true)
  }

  // UPDATE DATABASE DATA
  const handleChangeCardResponsibleClick = async (obj) => {
    try {
      const updatedDate = new Date()
      const cardRef = doc(db, 'cards', obj.ref.id)
      const originalCardDoc = await getDoc(cardRef)
      const originalCardData = originalCardDoc.data()
      const updateFields = {
        responsibleRef: doc(db, 'users', obj.responsibleId),
        updatedDate,
      }

      await setDoc(cardRef, updateFields, { merge: true })

      const updatedCardDoc = { ...obj, ...updateFields }
      dispatch(cardUpdated(updatedCardDoc))

      const newResponsibleDoc = await getUserById(obj.responsibleId)
      const newResponsibleEmail = newResponsibleDoc?.data()?.email
      if (typeof newResponsibleEmail === 'string' && newResponsibleEmail.trim() !== '') {
        const creatorId = originalCardData?.createdByRef?.id
        const creatorDoc = creatorId ? await getUserById(creatorId) : null
        const creatorName =
          `${creatorDoc?.data()?.firstname || ''} ${creatorDoc?.data()?.lastname || ''}`.trim() ||
          'Sistema'
        const location = originalCardData?.locationRef?.id
          ? await getLocationById(originalCardData.locationRef.id)
          : null
        const firstPhotoPath = originalCardData?.files?.[0]
        const firstVideoPath = originalCardData?.videos?.[0]
        const photoUrl = firstPhotoPath ? await getDownloadURL(ref(storage, firstPhotoPath)) : null
        const videoUrl = firstVideoPath ? await getDownloadURL(ref(storage, firstVideoPath)) : null
        const createdDate =
          typeof originalCardData?.createdDate?.toDate === 'function'
            ? originalCardData.createdDate.toDate()
            : originalCardData?.createdDate || updatedDate
        const message = mailNotificationsMessage(
          originalCardData?.type || obj.type,
          originalCardData?.description || '',
          createdDate,
          creatorName,
          location?.name || '',
          obj.ref.id,
          photoUrl,
          videoUrl,
          `${SITE_URL}/redirect-card/${obj.ref.id}`,
          { intent: 'assignment' }
        )
        await addDoc(collection(db, 'mailNotifications'), {
          message,
          to: [newResponsibleEmail],
        })
      }

      notifyToast('Tarjeta actualizada correctamente', 'success')
      setTimeout(() => setModalOpen(false), 1000)
    } catch (error) {
      console.error('Error al actualizar la tarjeta:', error)
      notifyToast('Ha ocurrido un error al actualizar la tarjeta', 'error')
    }
  }

  const handleUpdateCardClick = async (obj) => {
    try {
      const { ref, proposedImprovement, priority, instalationStatus, executor, extremeDatePlan } = obj
      const cardRef = doc(db, 'cards', ref.id)
      const updateFields = {
        proposedImprovement,
        priority,
        instalationStatus,
        executor,
        status: 1,
        extremeDatePlan,
        updatedDate: new Date(),
      }
      const originalCardDoc = await getDoc(cardRef)
      const originalCardData = originalCardDoc.data()
      await updateDoc(cardRef, updateFields)
      dispatch(cardUpdated({ id: ref.id, ...originalCardData, ...updateFields }))

      const creatorId = originalCardData?.createdByRef?.id
      if (creatorId) {
        const creatorDoc = await getUserById(creatorId)
        const creatorEmail = creatorDoc?.data()?.email
        if (typeof creatorEmail === 'string' && creatorEmail.trim() !== '') {
          const message = cardStatusChangeNotificationMessage(
            originalCardData?.type,
            ref.id,
            'En proceso',
            `${userData?.firstname || ''} ${userData?.lastname || ''}`.trim() || 'Sistema',
            new Date(),
            `${SITE_URL}/redirect-card/${ref.id}`
          )
          await addDoc(collection(db, 'mailNotifications'), {
            message,
            to: [creatorEmail],
          })
        }
      }
      notifyToast('Tarjeta actualizada correctamente', 'success')
      setModalOpen(false)
      dismissCardSurfacesAfterStatusChange()
    } catch (error) {
      console.error('Ha Error al actualizar la tarjeta:', error)
      notifyToast('Ha ocurrido un error al actualizar la tarjeta', 'error')
    }
  }

  const handleCompleteCardClick = async (obj) => {
    try {
      const { ref, validationDate, otNumber, improvementMade, comments } = obj
      const userRef = doc(db, 'users', userData.uid)
      const cardRef = doc(db, 'cards', ref.id)
      const updateFields = {
        validationDate,
        otNumber,
        improvementMade,
        status: 2,
        extremeDateReal: new Date(),
        updatedDate: new Date(),
        comments: comments || '',
        closingPersonRef: userRef,
        evidenceFiles: filesRef.current,
        evidenceVideos: videosRef.current,
      }
      const originalCardDoc = await getDoc(cardRef)
      const originalCardData = originalCardDoc.data()
      await updateDoc(cardRef, updateFields)
      dispatch(cardUpdated({ id: ref.id, ...originalCardData, ...updateFields }))

      const creatorId = originalCardData?.createdByRef?.id
      if (creatorId) {
        const creatorDoc = await getUserById(creatorId)
        const creatorEmail = creatorDoc?.data()?.email
        if (typeof creatorEmail === 'string' && creatorEmail.trim() !== '') {
          const message = cardStatusChangeNotificationMessage(
            originalCardData?.type,
            ref.id,
            'Cerrada',
            `${userData?.firstname || ''} ${userData?.lastname || ''}`.trim() || 'Sistema',
            new Date(),
            `${SITE_URL}/redirect-card/${ref.id}`
          )
          await addDoc(collection(db, 'mailNotifications'), {
            message,
            to: [creatorEmail],
          })
        }
      }
      notifyToast('Tarjeta completada correctamente', 'success')
      setModalOpen(false)
      dismissCardSurfacesAfterStatusChange()
    } catch (error) {
      console.error('Error al completar la tarjeta:', error)
      notifyToast('Ha ocurrido un error al completar la tarjeta', 'error')
    }
  }

  // ------------------------------------------------------------------------------------------- //

  useLayoutEffect(() => {
    dispatch(reload())
    dispatch(clearFilters())
  }, [dispatch])

  // update positions on scroll.
  useEffect(() => {
    if (typeof scrollRef.current === 'undefined') return // prevent build from failing.
    const el = scrollRef.current
    const onScroll = () => setOffsetTop(el.scrollTop)
    el.addEventListener('scroll', onScroll)

    return () => el.removeEventListener('scroll', onScroll)
  }, [])
  const maybeMoveOnScroll = offsetTop > HEADER_HEIGHT_DESKTOP
  const dektopOffsetTranslateY = HEADER_HEIGHT_DESKTOP - offsetTop

  // open preview frame on card-focus.
  const handleFocusedobject = (bool, obj) => {
    if (bool) return setFocusedObject(obj)
    return null
  }

  const handlePreviewClose = () => {
    setFocusedObject(null)
  }

  const handleMobileCardOpen = (card) => {
    const idx = filteredCards.findIndex((c) => c.id === card.id)
    setMobileCardStack(filteredCards)
    setMobileCardIndex(idx >= 0 ? idx : 0)
    setMobileCardStackKey('cards')
  }

  const handleMobileCardStackClose = () => {
    setMobileCardStackKey('')
    setMobileCardStack([])
    setMobileCardIndex(0)
  }

  // return content.
  return (
    <>
      <AdminTemplate
        ref={scrollRef}
        sectionTitle={navTitle}
        navigation={navMenu}
        isMobile={isMobile}
        modalOpen={modalOpen}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        filterComponents={[
          <SearchCardsById
            key="in-cards-search"
            items={filteredCards}
            defaultValue={searchTerm}
            onSearch={(term) => {
              dispatch(setFilters({ searchTerm: term }))
            }}
          />,
          <FilterDate
            key="in-cards-date"
            label="Filtrar en rango de fechas"
            items={filteredCards}
            defaultValue={{ fromDate: fromDateFilter, toDate: toDateFilter }}
            onDateFilter={(fromDate, toDate) => dispatch(setFilters({ fromDate, toDate }))}
          />,
          <FilterCardType
            key="in-cards-type"
            label="Tipo de Tarjeta"
            items={filteredCards}
            defaultValue={cardTypeFilter}
            onTypeFilter={(type) => dispatch(setFilters({ type }))}
          />,
          <FilterStatus
            key="in-cards-status"
            label="Estado"
            defaultValue={statusFilter}
            onStatusFilter={(status) => dispatch(setFilters({ status }))}
          />,
          <FilterLocation
            key="in-cards-location"
            label="Ubicación"
            placeholder="Buscar ubicaciones"
            value={locationFilter}
            items={filteredCards}
            locationsSelectTree={locationsTree}
            onLocationFilter={(locationId) => dispatch(setFilters({ locationId }))}
          />,
          <FilterResponsible
            key="in-cards-responsible"
            label="Responsable"
            placeholder="Responsable"
            items={filteredCards}
            responsibles={responsiblesSelectOptions}
            onResponsibleFilter={(responsibleId) => dispatch(setFilters({ responsibleId }))}
          />,
        ]}
        onOverlayClick={() => setModalOpen(false)}
        onCondensedChange={(isCondensed) => setNavIsCondensed(isCondensed)}
      >
        <Header justifyContent="flex-start">
          <ToggleFilterButton
            isActive={filtersOpen}
            onChange={(isOpen) => setFiltersOpen(isOpen)}
          />
          {isMobile ? (
            <ToggleSortingButton onChange={(isOpen) => setSortingOpen(isOpen)} />
          ) : (
            <FilterList
              items={activeFilters}
              onFilterClick={(filterValue) => dispatch(removeFilter(filterValue))}
              onClearAllClick={() => dispatch(clearFilters())}
            />
          )}
        </Header>

        {loaded === 'succeeded' ? (
          isMobile ? (
            <CardsMobileWrapper isEmpty={filteredCards.length <= 0}>
              <CardsMobileSort
                items={filteredCards}
                responsibles={users}
                isOpen={sortingOpen}
                onSort={(e) => dispatch(setFilters({ sort: e }))}
              />

              {filteredCards.map((card) => (
                <CardsMobileItem
                  key={card.id}
                  item={card}
                  onOpen={() => handleMobileCardOpen(card)}
                  onEdit={() => handleChangeCardResponsibleModal(card)}
                />
              ))}
            </CardsMobileWrapper>
          ) : (
            <CardsDesktopWrapper isEmpty={filteredCards.length <= 0}>
              <CardsDesktopSort
                items={filteredCards}
                responsibles={users}
                style={{
                  ...(maybeMoveOnScroll && {
                    maxWidth: `calc(100% - ${
                      filtersOpen
                        ? navIsCondensed
                          ? `${NAVIGATION_CONDENSED_WIDTH + FILTERS_ASIDE_WIDTH}px`
                          : `${NAVIGATION_WIDTH + FILTERS_ASIDE_WIDTH}px`
                        : navIsCondensed
                          ? `${NAVIGATION_CONDENSED_WIDTH}px`
                          : `${NAVIGATION_WIDTH}px`
                    })`,
                    position: 'fixed',
                  }),
                }}
                onSort={(e) => dispatch(setFilters({ sort: e }))}
              />

              {filteredCards.map((card, i) => (
                <CardsDesktopItem
                  key={card.id}
                  item={card}
                  isEven={i % 2 === 0}
                  onEdit={() => handleChangeCardResponsibleModal(card)}
                  onFocus={(bool) => handleFocusedobject(bool, card)}
                />
              ))}

              <PreviewFrame
                onCloseClick={handlePreviewClose}
                items={
                  !!focusedObject && [
                    <ReportCard
                      key={focusedObject.id}
                      type={focusedObject.type}
                      card={focusedObject}
                      onActionClick={focusedObject.status < 3 ? () => handleChangeState(focusedObject) : null}
                      actionLabel={
                        focusedObject.status === 0
                          ? 'Pasar a estado en proceso'
                          : focusedObject.status === 1 && 'Cerrar tarjeta'
                      }
                    />,
                  ]
                }
                style={
                  offsetTop > 0
                    ? {
                        height: maybeMoveOnScroll
                          ? '100vh'
                          : PREVIEWFRAME_HEIGHT(dektopOffsetTranslateY),
                        top: maybeMoveOnScroll ? 0 : `${dektopOffsetTranslateY}px`,
                      }
                    : {
                        height: PREVIEWFRAME_HEIGHT(),
                        top: HEADER_HEIGHT_DESKTOP,
                      }
                }
              />
            </CardsDesktopWrapper>
          )
        ) : (
          <Loader isPage />
        )}
      </AdminTemplate>

      <Drawer title={modalContent.title} isOpen={modalOpen} onChange={handleModalOpen}>
        {modalContent.component}
      </Drawer>

      {isMobile && (
        <ShowFrame
          layerZIndex={52}
          onCloseClick={handleMobileCardStackClose}
          activeStack={mobileCardStackKey}
          activeIndex={mobileCardIndex}
          disableKeyboardNavigation={modalOpen}
          items={
            mobileCardStack.length > 0
              ? mobileCardStack.map((item) => (
                  <ReportCard
                    key={item.id}
                    type={item.type}
                    card={item}
                    onActionClick={item.status < 3 ? () => handleChangeState(item) : null}
                    actionLabel={
                      item.status === 0
                        ? 'Pasar a estado en proceso'
                        : item.status === 1 && 'Cerrar tarjeta'
                    }
                  />
                ))
              : []
          }
        />
      )}
    </>
  )
}

export default AdminCardsScreen
